import {
  Box,
  Button,
  CircularProgress,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
  Table,
  TextField,
  Grid,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import axios from "../../utils/axiosInstance";
import AssignUnitDialog from "./assignUnitDialog";
import ConfirmDialog from "../ConfirmDialog";
import { toast } from "react-toastify";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";
import { formatDate } from "../../utils/formatDate";

const ReportGroupUnits = ({ group }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [groupUnits, setGroupUnits] = useState([]);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("Unit Name");
  const [searchQuery, setSearchQuery] = useState("");
  const [assignUnitDialogOpen, setAssignUnitDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState([]);

  const fetchUnits = async () => {
    if (!group?.id) return;
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/devices?groupId=${group.id}`
      );
      setGroupUnits(response.data || []);
    } catch (err) {
      console.error("Error fetching units:", err);
      setError("Failed to load units.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnits();
  }, [group?.id]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedUnitsData = useMemo(() => {
    if (!groupUnits) return [];

    return [...groupUnits].sort((a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case "Unit Name":
          aValue = a.name;
          bValue = b.name;
          break;
        case "Unit ID":
          aValue = a.id;
          bValue = b.id;
          break;
        default:
          return 0;
      }

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [groupUnits, order, orderBy]);

  const filteredUnitsData = sortedUnitsData.filter(
    (row) =>
      row.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleOpenAssignUnitDialog = async () => {
    setAssignUnitDialogOpen(true);
  };

  const handleDeleteClick = (unit) => {
    setUnitToDelete((prevUnits) => [...prevUnits, unit.id]);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (unitToDelete.length === 0) return;
    try {
      setConfirmOpen(false);
      const response = await axios.delete(
        `${process.env.REACT_APP_API_GATEAWAY}/assets/group/${group.id}`,
        {
          data: { deviceIds: unitToDelete,UI_Description: `${getUIDescription()}\n Delete Unit From report group`,  },
        }
      );
      if (response.status >= 200 && response.status < 300) {
        toast.success("Unit Successfully Deleted from Report Group");
        await fetchUnits();
        setUnitToDelete([]);
      } else {
        toast.error("Failed to delete unit from report group");
      }
    } catch (err) {
      console.error("❌ Error deleting unit from report group:", err);
      toast.error("Failed to delete unit from report group");
    }
  };

  const renderUnitsTable = () => (
    <TableContainer
      sx={{
        flexGrow: 1,
        height: "63vh",
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
            {["Unit Name", "Unit ID", "Created date"].map((col) => (
              <TableCell key={col} sx={{ fontSize: 15 }}>
                <TableSortLabel
                  active={orderBy === col}
                  direction={orderBy === col ? order : "asc"}
                  onClick={() => handleRequestSort(col)}
                >
                  {col}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredUnitsData.length > 0 ? (
            filteredUnitsData.map((unit, index) => (
              <TableRow
                key={unit.id}
                hover
                sx={{
                  backgroundColor: index % 2 === 0 ? "#2f3136" : "#3a3c43",
                  "&:hover": {
                    backgroundColor: "#4a4d54",
                  },
                }}
              >
                <TableCell sx={{ fontSize: 15 }}>{unit.name}</TableCell>
                <TableCell sx={{ fontSize: 15 }}>{unit.id}</TableCell>
                <TableCell>
                  {/* <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={() => handleDeleteClick(unit)}
                  >
                    Delete
                  </Button> */}
                  {formatDate(unit.assetCreatedDate)}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                No units found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ mt: 2, mx: 1 }}>
      {loading ? (
        <Box
          sx={{
            width: "100%",
            minHeight: "200px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            zIndex: 10,
          }}
        >
          <CircularProgress color="#60a5fa" size={60} />
        </Box>
      ) : error ? (
        <Box
          sx={{
            width: "100%",
            textAlign: "center",
            color: "red",
            fontWeight: "bold",
            py: 2,
          }}
        >
          {error}
        </Box>
      ) : (
        <>
          <Grid
            container
            spacing={{ xs: 2, md: 0 }}
            justifyContent={"space-between"}
            alignItems={"flex-end"}
            mb={2}
          >
            <Grid container size={{ xs: 12, md: 6 }}>
              <TextField
                label="Search Unit..."
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ minWidth: 200, width: 300 }}
              />
            </Grid>
            <Grid
              container
              gap={1}
              size={{ xs: 12, md: 6 }}
              justifyContent={"flex-end"}
            >
              <Button variant="contained" color="primary" onClick={fetchUnits}>
                Refresh
              </Button>
              {/* <Button
                variant="outlined"
                color="primary"
                onClick={handleOpenAssignUnitDialog}
              >
                Assign Unit
              </Button> */}
            </Grid>
          </Grid>
          {renderUnitsTable()}
        </>
      )}
      <AssignUnitDialog
        open={assignUnitDialogOpen}
        onClose={() => setAssignUnitDialogOpen(false)}
        groupId={group?.id}
        groupName={group?.name}
        fetchUnits={fetchUnits}
      />
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
};

export default ReportGroupUnits;
